// v5.4.0 · Evaluación post-clase: 60–120 min después de terminar cada clase el estudiante responde 4 escalas y 2 textos cortos.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardCheck, CheckCircle2, Loader2 } from "lucide-react";

const FACES = ["😟", "😕", "😐", "🙂", "😄"];

export default function EvaluacionClase() {
  const [data, setData] = useState(null);
  const [current, setCurrent] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/student-extras/class-evals/mine").then(({ data: d }) => {
    setData(d);
    const pend = (d.items || []).filter((i) => i.status === "pending");
    setCurrent((c) => (c && pend.find((p) => p.id === c.id)) || pend[0] || null);
  }).catch(() => setData({ items: [], questions: [] }));
  useEffect(() => { load(); }, []);

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/student-extras/class-evals/${current.id}/submit`, { answers });
      toast.success("¡Gracias! Tu docente verá tus respuestas.");
      setAnswers({});
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo enviar. Intenta de nuevo.");
    } finally { setBusy(false); }
  };

  if (!data) return <p className="text-sm text-[#6E7591]"><Loader2 size={14} className="inline animate-spin mr-1" /> Cargando…</p>;
  const pending = data.items.filter((i) => i.status === "pending");
  const done = data.items.filter((i) => i.status === "completed");
  const scales = data.questions.filter((q) => q.type === "scale");
  const ready = current && scales.every((q) => answers[q.id]);

  return (
    <div className="space-y-5 max-w-2xl" data-testid="evaluacion-clase">
      <div>
        <p className="text-[11px] tracking-[0.2em] uppercase font-extrabold text-[#05A859]">Clases en vivo</p>
        <h1 className="font-brand text-3xl text-[#1F2547] inline-flex items-center gap-2"><ClipboardCheck className="text-[#05A859]" size={28} /> Evalúa tu clase</h1>
        <p className="text-sm text-[#6E7591]">Dos minutos para contarle a tu docente cómo te fue.</p>
      </div>

      {!current ? (
        <div className="neo-card p-8 text-center" data-testid="evals-vacio">
          <CheckCircle2 size={36} className="mx-auto text-[#05A859] mb-2" />
          <p className="font-bold text-[#1F2547]">No tienes evaluaciones pendientes 🎉</p>
          <p className="text-sm text-[#6E7591] mt-1">Aparecerán aquí poco después de cada clase.</p>
        </div>
      ) : (
        <div className="neo-card p-5 space-y-5">
          {pending.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {pending.map((p) => (
                <button key={p.id} type="button" onClick={() => { setCurrent(p); setAnswers({}); }}
                  className={`text-xs font-extrabold px-3 py-1.5 rounded-full ${p.id === current.id ? "neo-pressed text-[#A43790]" : "neo-flat text-[#6E7591]"}`}>{p.class_title || "Clase"}</button>
              ))}
            </div>
          )}
          <p className="font-brand text-lg text-[#1F2547]">{current.class_title || "Tu clase"}</p>
          {data.questions.map((q) => (
            <div key={q.id}>
              <p className="text-sm font-bold text-[#1F2547] mb-2">{q.label}</p>
              {q.type === "scale" ? (
                <div className="flex gap-2" role="radiogroup" aria-label={q.label}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => setAnswers((a) => ({ ...a, [q.id]: n }))}
                      data-testid={`eval-${q.id}-${n}`} aria-pressed={answers[q.id] === n}
                      className={`w-12 h-12 rounded-2xl text-xl grid place-items-center transition-all ${answers[q.id] === n ? "neo-pressed ring-2 ring-[#A43790]" : "neo-flat"}`}>
                      {q.id === "ritmo" ? n : FACES[n - 1]}
                    </button>
                  ))}
                </div>
              ) : (
                <textarea rows={2} maxLength={400} value={answers[q.id] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                  data-testid={`eval-${q.id}`} className="w-full px-3 py-2 text-sm rounded-xl border-2 border-[#E1E5F0]" placeholder="Escribe aquí (opcional)" />
              )}
            </div>
          ))}
          <button type="button" onClick={send} disabled={!ready || busy} data-testid="eval-enviar" className="jasq-btn-primary inline-flex items-center gap-2 text-sm disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Enviar evaluación
          </button>
        </div>
      )}

      {done.length > 0 && (
        <p className="text-xs text-[#6E7591]">Ya respondiste {done.length} evaluación{done.length === 1 ? "" : "es"}. ¡Gracias por ayudarnos a mejorar!</p>
      )}
    </div>
  );
}
